import { useApi, PaginatedResponse } from '@/api';
import { EtherscanLink, Link, TableCellBottom } from '@/components';
import {
  CurrencyValue,
  DataTable,
  Flex,
  WalletAddress,
  RelativeDateTime,
  TableSkeleton,
  useTableProps,
  TextSearchFilter,
  DropdownFilter
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import { useParams } from 'react-router';

type Liquidation = {
  datetime: string;
  tx_hash: string;
  debt_underlying_address: string;
  collateral_underlying_address: string;
  wallet_address: string;
  liquidator_address: string;
  debt_underlying_symbol: string;
  debt_repaid: string;
  debt_repaid_usd: string;
  collateral_underlying_symbol: string;
  collateral_seized: string;
  collateral_seized_usd: string;
  liquidation_bonus_usd: string;
  order_index: string;
};

type AssetReference = {
  label: string;
  value: string;
};

type PaginatedResponseAdditional<T> = PaginatedResponse<T[]> & {
  collateral_assets: AssetReference[];
  debt_assets: AssetReference[];
};

type LiquidationsData = Liquidation;

export default function LiquidationsTable() {
  const { network } = useParams() as { network: string };

  const { pagination, sorting, filters } = useTableProps({
    pageSize: 10,
    initialSorting: '-order_index',
    filtersDebounceTime: 500,
    initialFilters: {
      textSearch: '',
      selectedCollateralOption: '',
      selectedDebtOption: ''
    }
  });
  const [data, error] = useApi<PaginatedResponseAdditional<LiquidationsData>>({
    url: '/liquidations/',
    queryParams: {
      page: pagination.currentPage,
      limit: pagination.pageSize,
      sort: sorting.currentSorting,
      search: filters.filtersApplied.textSearch,

      collateral_assets:
        filters.filtersApplied.selectedCollateralOption.toString(),
      debt_assets: filters.filtersApplied.selectedDebtOption.toString()
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={10} columns={6} />;
  }
  const columns: TableColumn[] = [
    {
      header: 'Wallet',
      getCell: (row) => (
        <Link
          key={row.wallet_address}
          to={`../wallets/${row.wallet_address}`}
          relative="path">
          <WalletAddress address={row.wallet_address} />
        </Link>
      )
    },
    {
      header: 'Collateral Liquidated',
      getCell: (row) => (
        <CurrencyValue
          value={row.collateral_seized}
          options={{
            currency: {
              name: row.collateral_underlying_symbol,
              address: row.collateral_underlying_address,
              network: network
            }
          }}
        />
      ),
      getCellBottom: (row) => (
        <TableCellBottom checkValue={row.collateral_seized_usd}>
          <CurrencyValue
            value={row.collateral_seized_usd}
            options={{
              currency: 'USD'
            }}
          />
        </TableCellBottom>
      ),
      sortingField: 'collateral_seized_usd',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Debt Repaid',
      getCell: (row) => (
        <CurrencyValue
          value={row.debt_repaid}
          options={{
            currency: {
              name: row.debt_underlying_symbol,
              address: row.debt_underlying_address,
              network: network
            }
          }}
        />
      ),
      getCellBottom: (row) => (
        <TableCellBottom checkValue={row.debt_repaid_usd}>
          <CurrencyValue
            value={row.debt_repaid_usd}
            options={{
              currency: 'USD'
            }}
          />
        </TableCellBottom>
      ),
      sortingField: 'debt_repaid_usd',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Liquidator',
      getCell: (row) => (
        <Link
          key={row.liquidator_address}
          to={`./liquidators/${row.liquidator_address}`}
          relative="path">
          <WalletAddress address={row.liquidator_address} />
        </Link>
      )
    },
    {
      header: 'Liquidation Bonus',
      getCell: (row) => (
        <CurrencyValue
          value={row.liquidation_bonus_usd}
          options={{
            currency: 'USD'
          }}
        />
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'liquidation_bonus_usd'
    },
    {
      header: 'Time',
      getCell: (row) => (
        <Flex gap="xs" color="muted">
          <RelativeDateTime value={row.datetime} />
          <EtherscanLink value={row.tx_hash} network={network} />
        </Flex>
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'datetime'
    }
  ];

  return (
    <Flex gap="m" flexDirection="column" justifyContent="flex-start">
      <Flex alignItems="center" justifyContent={['center', 'flex-end']}>
        <Flex flexDirection={['column', 'row']} gap={['s', 'xs']}>
          <Flex gap={['s', 'xs']}>
            <DropdownFilter
              multi={true}
              withCryptoIcon={true}
              filterField="selectedCollateralOption"
              options={data.collateral_assets || []}
              pagination={pagination}
              titleFormat={(filteredCount: number) =>
                `Collateral asset (${filteredCount})`
              }
              {...filters}
            />
            <DropdownFilter
              multi={true}
              withCryptoIcon={true}
              filterField="selectedDebtOption"
              options={data.debt_assets || []}
              pagination={pagination}
              titleFormat={(filteredCount) => `Debt asset (${filteredCount})`}
              {...filters}
            />
          </Flex>
          <TextSearchFilter
            pagination={pagination}
            placeholder="Search by wallet"
            display="flex"
            flex="1"
            options={{
              variants: {
                textSearchFilter: 'fullWidth'
              }
            }}
            {...filters}
          />
        </Flex>
      </Flex>
      <DataTable
        rows={data.results}
        columns={columns}
        getKey={(row) => `${row.order_index}`}
        sorting={sorting}
        pagination={{
          ...pagination,
          totalRecords: data.pagination.total
        }}
        width="100%"
      />
    </Flex>
  );
}
